import React, { useState } from 'react';

const MISSING = '--';

function formatCurrency(value) {
  if (value == null || isNaN(value)) return MISSING;
  return `$${Number(value).toFixed(2)}`;
}

function formatPercent(value, decimals = 1) {
  if (value == null || isNaN(value)) return MISSING;
  return `${Number(value).toFixed(decimals)}%`;
}

function formatNumber(value) {
  if (value == null || isNaN(value)) return MISSING;
  return Number(value).toLocaleString();
}

/**
 * Drop % coloring: red for a decline, green for a gain, opacity scaling with
 * magnitude so the deepest discounts are visually obvious. A null is rendered
 * as a muted dash and given NO background -- never as 0%, which would read as
 * "unchanged" when the truth is "unknown".
 */
function getDropColor(value) {
  if (value == null || isNaN(value)) return {};
  const magnitude = Math.min(Math.abs(Number(value)) / 80, 1);
  const alpha = 0.12 + magnitude * 0.45;
  return Number(value) < 0
    ? { backgroundColor: `rgba(239, 68, 68, ${alpha})` }
    : { backgroundColor: `rgba(34, 197, 94, ${alpha})` };
}

function ScalpingResultsTable({ data, totalFound, truncated, onAddToWatchlist, watchedStocks }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterTicker, setFilterTicker] = useState('all');
  const [filterExpiration, setFilterExpiration] = useState('all');
  const [hideUntraded, setHideUntraded] = useState(false);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No contracts matched your filters</p>
        <p className="text-sm text-gray-500 mt-2">
          Try widening the expiration window, raising the price ceiling, or lowering the
          minimum drop.
        </p>
      </div>
    );
  }

  const isWatched = (ticker) =>
    watchedStocks?.some((stock) => stock.ticker === ticker) || false;

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const SortableHeader = ({ field, label, title }) => (
    <th
      className="text-right py-2 px-2 cursor-pointer hover:opacity-80 whitespace-nowrap"
      style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
      onClick={() => handleSort(field)}
      title={title}
    >
      {label} {sortField === field && (sortDirection === 'asc' ? '↑' : '↓')}
    </th>
  );

  const uniqueTickers = [...new Set(data.map((r) => r.underlying_ticker))];
  const uniqueExpirations = [...new Set(data.map((r) => r.expiration))].sort();

  // Bid/ask are absent on this data plan; only render those columns if the
  // response actually carries them, rather than showing two dead columns.
  const hasQuotes = data.some((r) => r.bid != null || r.ask != null);

  let filtered = data;
  if (filterTicker !== 'all') {
    filtered = filtered.filter((r) => r.underlying_ticker === filterTicker);
  }
  if (filterExpiration !== 'all') {
    filtered = filtered.filter((r) => r.expiration === filterExpiration);
  }
  if (hideUntraded) {
    filtered = filtered.filter((r) => !r.is_untraded);
  }

  // Null-last sorting, matching the backend's ordering guarantee: a missing
  // value must never sort as though it were an extreme one.
  const sorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const comparison = aVal > bVal ? 1 : -1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const untradedCount = data.filter((r) => r.is_untraded).length;
  const staleCount = data.filter((r) => r.is_stale).length;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Filter by Ticker</label>
          <select
            value={filterTicker}
            onChange={(e) => setFilterTicker(e.target.value)}
            className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          >
            <option value="all">All Tickers</option>
            {uniqueTickers.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Filter by Expiration</label>
          <select
            value={filterExpiration}
            onChange={(e) => setFilterExpiration(e.target.value)}
            className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          >
            <option value="all">All Expirations</option>
            {uniqueExpirations.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
        {untradedCount > 0 && (
          <label className="flex items-center gap-2 cursor-pointer pb-1"
                 title="Contracts with no volume today. Their last mark may be carried forward, so an apparent discount can be an artifact.">
            <input
              type="checkbox"
              checked={hideUntraded}
              onChange={(e) => setHideUntraded(e.target.checked)}
              className="w-4 h-4 accent-blue-500"
            />
            <span className="text-xs text-gray-300">
              Hide untraded ({untradedCount})
            </span>
          </label>
        )}
      </div>

      {truncated && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-md px-4 py-2 text-sm text-yellow-300">
          Showing the top {data.length} of {totalFound} matches. Narrow your filters to see
          the rest.
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
              <th
                className="text-left py-2 px-2 sticky left-0 z-10"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
                colSpan="4"
              >
                CONTRACT
              </th>
              <th
                className="text-center py-2 px-2"
                style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }}
                colSpan={hasQuotes ? 5 : 3}
              >
                THE DISCOUNT
              </th>
              <th
                className="text-center py-2 px-2"
                style={{ backgroundColor: '#1a1a1a', color: '#888888', borderLeft: '1px solid #333' }}
                colSpan="5"
              >
                CONTEXT
              </th>
              <th
                className="text-center py-2 px-2 sticky right-0 z-10"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
              >
                Actions
              </th>
            </tr>
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
              <th
                className="text-left py-2 px-2 sticky left-0 z-10"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
              >
                Ticker
              </th>
              <SortableHeader field="strike" label="Strike" />
              <SortableHeader field="expiration" label="Expiration" />
              <SortableHeader field="days_to_expiration" label="DTE" title="Days to expiration" />

              <SortableHeader field="last_price" label="Price" title="Current price used for the comparison" />
              <SortableHeader field="previous_close" label="Prev Close" title="Yesterday's closing price for this contract" />
              <SortableHeader field="drop_percent" label="Drop %" title="Change from yesterday's close. Negative means down." />
              {hasQuotes && <SortableHeader field="bid" label="Bid" />}
              {hasQuotes && <SortableHeader field="ask" label="Ask" />}

              <SortableHeader field="volume" label="Volume" title="Contracts traded today" />
              <SortableHeader field="open_interest" label="OI" title="Open interest" />
              <SortableHeader field="otm_percent" label="OTM %" title="Positive is out-of-the-money" />
              <SortableHeader field="underlying_price" label="Underlying" />
              <SortableHeader field="trade_age_seconds" label="Data Age" title="How long ago this contract's price last updated" />

              <th
                className="text-center py-2 px-2 sticky right-0 z-10"
                style={{ backgroundColor: '#1a1a1a' }}
              ></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, index) => {
              const muted = row.is_stale;
              return (
                <tr
                  key={row.option_ticker || index}
                  className="border-b border-gray-800 hover:bg-gray-700/30"
                  style={muted ? { opacity: 0.55 } : {}}
                >
                  <td className="py-2 px-2 font-medium text-white sticky left-0 bg-gray-800">
                    {isWatched(row.underlying_ticker) && <span className="mr-1">&#11088;</span>}
                    {row.underlying_ticker}
                    <span className="ml-1 text-gray-500 uppercase">
                      {row.contract_type === 'put' ? 'P' : 'C'}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-right text-white">
                    {formatCurrency(row.strike)}
                  </td>
                  <td className="py-2 px-2 text-right text-white whitespace-nowrap">
                    {row.expiration}
                  </td>
                  <td className="py-2 px-2 text-right text-white">
                    {row.days_to_expiration}
                  </td>

                  <td
                    className="py-2 px-2 text-right text-white"
                    style={{ borderLeft: '1px solid #333' }}
                    title={`Price source: ${row.price_source || 'unknown'}`}
                  >
                    {formatCurrency(row.last_price)}
                  </td>
                  <td className="py-2 px-2 text-right text-gray-300">
                    {row.previous_close == null ? (
                      <span className="text-gray-600">{MISSING}</span>
                    ) : (
                      formatCurrency(row.previous_close)
                    )}
                  </td>
                  <td
                    className="py-2 px-2 text-right font-bold text-white"
                    style={getDropColor(row.drop_percent)}
                  >
                    {row.drop_percent == null ? (
                      <span
                        className="text-gray-600 font-normal"
                        title="No prior close available for this contract, so no discount can be computed."
                      >
                        {MISSING}
                      </span>
                    ) : (
                      formatPercent(row.drop_percent)
                    )}
                  </td>
                  {hasQuotes && (
                    <td className="py-2 px-2 text-right text-white">{formatCurrency(row.bid)}</td>
                  )}
                  {hasQuotes && (
                    <td className="py-2 px-2 text-right text-white">{formatCurrency(row.ask)}</td>
                  )}

                  <td
                    className="py-2 px-2 text-right text-white"
                    style={{ borderLeft: '1px solid #333' }}
                  >
                    {row.is_untraded ? (
                      <span
                        className="text-orange-400"
                        title="No volume today — this contract has not traded, so an apparent discount may be an artifact of a carried-forward mark."
                      >
                        0 ⚠
                      </span>
                    ) : (
                      formatNumber(row.volume)
                    )}
                  </td>
                  <td className="py-2 px-2 text-right text-gray-300">
                    {formatNumber(row.open_interest)}
                  </td>
                  <td className="py-2 px-2 text-right text-white">
                    {formatPercent(row.otm_percent)}
                  </td>
                  <td className="py-2 px-2 text-right text-gray-300">
                    {formatCurrency(row.underlying_price)}
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    {row.is_stale ? (
                      <span
                        className="text-yellow-400"
                        title="This price has not updated in over a trading day. A discount measured against a stale mark is not a real discount."
                      >
                        ⚠ {row.data_age}
                      </span>
                    ) : (
                      <span className="text-gray-400">{row.data_age}</span>
                    )}
                  </td>

                  <td className="py-2 px-2 text-center sticky right-0 bg-gray-800">
                    {!isWatched(row.underlying_ticker) && onAddToWatchlist && (
                      <button
                        onClick={() => onAddToWatchlist(row.underlying_ticker)}
                        className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded border border-blue-400 hover:border-blue-300"
                      >
                        Watch
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary + legend */}
      <div className="flex flex-wrap justify-between items-center gap-3 text-sm text-gray-400">
        <div>
          Showing {sorted.length} of {data.length} contracts
          {totalFound > data.length && ` (${totalFound} matched)`}
        </div>
        <div className="flex flex-wrap gap-4 text-xs">
          <span>
            <span
              className="inline-block w-4 h-3 mr-1 rounded align-middle"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.45)' }}
            ></span>
            Deeper red = bigger discount
          </span>
          {untradedCount > 0 && <span className="text-orange-400">⚠ 0 volume = untraded today</span>}
          {staleCount > 0 && <span className="text-yellow-400">⚠ age = stale price</span>}
        </div>
      </div>

      {/* Interpretation guide */}
      <div className="bg-gray-700/30 rounded-lg p-4 mt-2">
        <h4 className="text-sm font-medium text-gray-300 mb-2">Reading the Results</h4>
        <ul className="text-xs text-gray-400 space-y-1">
          <li>• <strong>Drop %</strong> compares this contract&apos;s own price against its prior close — the dislocation you&apos;re hunting, not a move in the stock.</li>
          <li>• A <strong>dash</strong> in Prev Close or Drop % means no prior close was available. It is not a zero.</li>
          <li>• <strong>0 volume ⚠</strong> is the classic false positive: nothing traded today, so the markdown may be an artifact rather than a real dislocation.</li>
          <li>• <strong>DTE</strong> is your margin for the thesis to work. A steep discount on something expiring imminently is usually just time decay.</li>
        </ul>
      </div>
    </div>
  );
}

export default ScalpingResultsTable;
